import { format, isBefore, parseISO } from 'date-fns';
import ruLocale from 'date-fns/locale/ru';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { getEntityUrl } from '@culture/common-helpers/cultureUtils';
import { broadcastTypeTitlesHash } from '@culture/utils/helpers/broadcast';
import Breadcrumbs from 'components/Breadcrumbs';
import DetailEntityPreviewLabels from 'components/DetailEntityPreviewLabels';
import FavoritesButtonContainer from 'containers/FavoritesButton';
import PlayButton from 'components/PlayButton';
import Place from 'components/Place';
import Container from 'components/Container';
import VideoContainer from 'components/VideoContainer';
import ShowOnlyClient from 'components/ShowOnlyClient';
import Tag from 'components/Tag';

import CommonPropTypes from 'utils/propTypes/common';
import {
	DISCLAIMERS_HASH,
	MISSING_VIDEO_STUB_TEXT,
	isYoutubePlayer,
	makeMovieSlides
} from 'utils/helpers/movies';

import CommonDisclaimer from 'components/CommonDisclaimer';
import styles from './styles.module.scss';

const BroadcastPreview = ({ breadcrumbs, data, theme }) => {
	const disclaimerRef = useRef(null);
	const actionsRef = useRef(null);
	const {
		_id,
		ageRestriction,
		title,
		institute,
		broadcastType,
		materials,
		withVideo,
		playerType
	} = data;

	const showYouTubeDisclaimer = isYoutubePlayer({
		materials,
		playerType,
		withVideo
	});

	const publishDate = parseISO(data.publishDate);
	const toDate = parseISO(data.toDate);

	const broadcastUrl = getEntityUrl('broadcast', data);
	const instituteUrl =
		institute?.status === 'published'
			? getEntityUrl('streamingPlatforms', institute)
			: null;
	const slides = makeMovieSlides(materials, data.type);
	const isRemoveFromMain = isBefore(toDate, new Date());

	// eslint-disable-next-line consistent-return
	useEffect(() => {
		if (disclaimerRef?.current) {
			const disclaimerResizeObserver = new ResizeObserver((entries) => {
				// eslint-disable-next-line no-restricted-syntax
				for (const entry of entries) {
					const { borderBoxSize } = entry;
					const height = borderBoxSize[0].blockSize;

					actionsRef.current.style.setProperty(
						'--disclaimer-offset',
						`${height}px`
					);
				}
			});
			const currentCopy = disclaimerRef.current;
			disclaimerResizeObserver.observe(currentCopy);

			return () => {
				disclaimerResizeObserver.disconnect(currentCopy);
			};
		}
	}, []);

	return (
		<VideoContainer
			{...data}
			theme={theme}
			withDisclaimer={showYouTubeDisclaimer}
			slides={slides}
		>
			{(onPlayButtonClick, slide) => (
				<Container
					className={cx(styles.preview, {
						[styles.preview_withDisclaimer]: showYouTubeDisclaimer
					})}
					innerClassName={styles.inner}
				>
					<div className={styles.breadcrumbs}>
						<Breadcrumbs items={breadcrumbs} />
					</div>
					<DetailEntityPreviewLabels ageRestriction={ageRestriction} />
					<h1 className={styles.title}>{title}</h1>
					<ShowOnlyClient>
						<div className={styles.list}>
							{isRemoveFromMain ? (
								<div className={styles.listItem}>
									{'Трансляция завершилась '}
									{format(toDate, 'd MMMM yyyy', { locale: ruLocale })}
								</div>
							) : (
								<>
									<div className={styles.listItem}>
										{format(publishDate, 'd MMMM yyyy', { locale: ruLocale })}
									</div>
									<div className={styles.listItem}>
										{format(publishDate, 'EEEEEE HH:mm', { locale: ruLocale })}
									</div>
								</>
							)}
						</div>
					</ShowOnlyClient>
					{!withVideo && isRemoveFromMain && (
						<div className={styles.text}>{MISSING_VIDEO_STUB_TEXT}</div>
					)}
					<div className={styles.row}>
						{broadcastType && (
							<div className={styles.labels}>
								<Tag
									title={broadcastTypeTitlesHash[broadcastType]}
									url={`/archive/type-${broadcastType}`}
									theme="bright"
								/>
							</div>
						)}
						<div ref={actionsRef} className={styles.actions}>
							<FavoritesButtonContainer
								title={title}
								entityId={_id}
								entityType="movies"
								type="broadcast"
							/>
						</div>
					</div>
					{(withVideo || !isRemoveFromMain) && (
						<div className={styles.more} data-player="start">
							<PlayButton
								key={broadcastUrl}
								publishDate={data.publishDate}
								toDate={data.toDate}
								onClick={onPlayButtonClick}
								text={slide && slide.title}
							/>
						</div>
					)}
					{institute && (
						<div className={styles.row}>
							<Place title={institute.title} size="medium" url={instituteUrl} />
						</div>
					)}
					{showYouTubeDisclaimer && (
						<div ref={disclaimerRef}>
							<CommonDisclaimer
								className={styles.disclaimer}
								text={DISCLAIMERS_HASH.youtube}
							/>
						</div>
					)}
				</Container>
			)}
		</VideoContainer>
	);
};

BroadcastPreview.propTypes = {
	data: CommonPropTypes.entity.isRequired,
	breadcrumbs: CommonPropTypes.breadcrumbs.isRequired,
	theme: PropTypes.oneOf(['entity'])
};

export default BroadcastPreview;
